import { ICopyable } from '../core/copyable.interface';
import { Dictionary } from '../core/dictionary_T';
import { IIdentifiable } from '../core/identifiable.interface';
import { Colors } from './enums/colors';
import { LockedType } from './enums/locked-type';
import { FolderWorkflowStepsSnapshot } from './folder-workflow-steps-snapshot';
import { ICustomAttributes } from './interface/custom-attributes.interface';
import { IFolder } from './interface/folder.interface';

/**
 * Represente un dossier
 */
export class Folder implements ICopyable<Folder>, IFolder, IIdentifiable {
    _id: any;

    get id(): any {
        return this._id;
    }

    set id(value: any) {
        this._id = value;
    }

    organizationId: string;

    lockedBy: string;

    lockedType: LockedType;

    lastConsultationDate: Date;

    folderStateId: string;

    customerId: number;

    boxId: string;

    name: string;

    isRead: boolean;

    creationDate: Date;

    updateDate: Date;

    isDelete: boolean;

    color: Colors;

    viewId: number;

    hasDocument: boolean;

    hasComment: boolean;

    hasNotification: boolean;

    hasWarning: boolean;

    isInStandByState: boolean;

    standByStart: Date;

    standByDuration: number;

    archivingDate: Date;

    isInWaitingArchivingState: boolean;

    isFollowed: boolean;

    private _workflowStepsSnapshot: FolderWorkflowStepsSnapshot[];

    get workflowStepsSnapshot(): FolderWorkflowStepsSnapshot[] {
        return this._workflowStepsSnapshot;
    }

    set workflowStepsSnapshot(value: FolderWorkflowStepsSnapshot[]) {
        this._workflowStepsSnapshot = value;

        if (value) {
            value.forEach((v) => {
                this.snapshotByStepId.set(v.workflowStepId.toString(), v);
            });
        }
    }

    /**
     * Attribut custom
     */
    customAttributes: ICustomAttributes;

    /**
     * Propriété propre au web
     */
    expanded: boolean = false;

    get isLocked(): boolean {
        return this.lockedType !== LockedType.NotLocked;
    }

    viewName: string;

    /**
     * Propriété dynamique
     */
    displayedDatas = new Dictionary<string>();
    snapshotByStepId = new Dictionary<FolderWorkflowStepsSnapshot>();

    public createCopy(): Folder {
        const instance = new Folder();
        instance.id = this.id;
        instance.organizationId = this.organizationId;
        instance.lockedBy = this.lockedBy;
        instance.lockedType = this.lockedType;
        instance.lastConsultationDate = this.lastConsultationDate;
        instance.folderStateId = this.folderStateId;
        instance.customerId = this.customerId;
        instance.name = this.name;
        instance.isRead = this.isRead;
        instance.creationDate = this.creationDate;
        instance.updateDate = this.updateDate;
        instance.isDelete = this.isDelete;
        instance.color = this.color;
        instance.viewId = this.viewId;
        instance.boxId = this.boxId;
        instance.hasDocument = this.hasDocument;
        instance.hasComment = this.hasComment;
        instance.hasNotification = this.hasNotification;
        instance.hasWarning = this.hasWarning;
        instance.isInStandByState = this.isInStandByState;
        instance.standByStart = this.standByStart;
        instance.standByDuration = this.standByDuration;
        instance.archivingDate = this.archivingDate;
        instance.isInWaitingArchivingState = this.isInWaitingArchivingState;
        instance.workflowStepsSnapshot = FolderWorkflowStepsSnapshot.fromArray(this.workflowStepsSnapshot);
        instance.customAttributes = this.customAttributes;
        instance.expanded = this.expanded;
        instance.isFollowed = this.isFollowed;
        instance.viewName = this.viewName;

        return instance;
    }

    public copyFrom(other: any) {
        this.id = other.id;
        this.organizationId = other.organizationId;
        this.lockedBy = other.lockedBy;
        this.lockedType = other.lockedType;
        this.lastConsultationDate = other.lastConsultationDate;
        this.folderStateId = other.folderStateId;
        this.customerId = other.customerId;
        this.name = other.name;
        this.isRead = other.isRead;
        this.creationDate = other.creationDate;
        this.updateDate = other.updateDate;
        this.isDelete = other.isDelete;
        this.color = other.color;
        this.viewId = other.viewId;
        this.boxId = other.boxId;
        this.hasDocument = other.hasDocument;
        this.hasComment = other.hasComment;
        this.hasNotification = other.hasNotification;
        this.hasWarning = other.hasWarning;
        this.isInStandByState = other.isInStandByState;
        this.standByStart = other.standByStart;
        this.standByDuration = other.standByDuration;
        this.archivingDate = other.archivingDate;
        this.isInWaitingArchivingState = other.isInWaitingArchivingState;
        this.workflowStepsSnapshot = FolderWorkflowStepsSnapshot.fromArray(other.workflowStepsSnapshot);
        this.customAttributes = other.customAttributes;
        this.expanded = other.expanded;
        this.isFollowed = other.isFollowed;
        this.viewName = other.viewName;
    }

    /**
     * Retourne un tableau d'instances de Folder à partir d'un tableau d'instances de IFolder.
     * @param other : Tableau d'instances de IFolder.
     * @returns : Tableau d'instances de Folder.
     */
    public static fromArray(others: IFolder[]) {
        if (!!others) {
            let folders: Folder[] = [];

            others.forEach((x) => {
                const folder = Folder.from(x);
                folders.push(folder);
            });

            return folders;
        } else {
            return null;
        }
    }

    /**
     * Retourne une instance de Folder à partir d'une instance de IFolder.
     * @param other : Instance de IFolder.
     * @returns : Instance de Folder.
     */
    public static from(other: IFolder) {
        if (!!other) {
            const instance = new Folder();

            instance.id = other.id;
            instance.organizationId = other.organizationId;
            instance.lockedBy = other.lockedBy;
            instance.lockedType = other.lockedType;
            instance.lastConsultationDate = other.lastConsultationDate;
            instance.folderStateId = other.folderStateId;
            instance.customerId = other.customerId;
            instance.name = other.name;
            instance.isRead = other.isRead;
            instance.creationDate = other.creationDate;
            instance.updateDate = other.updateDate;
            instance.isDelete = other.isDelete;
            instance.color = other.color;
            instance.viewId = other.viewId;
            instance.boxId = other.boxId;
            instance.hasDocument = other.hasDocument;
            instance.hasComment = other.hasComment;
            instance.hasNotification = other.hasNotification;
            instance.hasWarning = other.hasWarning;
            instance.isInStandByState = other.isInStandByState;
            instance.standByStart = other.standByStart;
            instance.standByDuration = other.standByDuration;
            instance.archivingDate = other.archivingDate;
            instance.isInWaitingArchivingState = other.isInWaitingArchivingState;
            instance.workflowStepsSnapshot = FolderWorkflowStepsSnapshot.fromArray(other.workflowStepsSnapshot);
            instance.customAttributes = other.customAttributes;
            instance.isFollowed = other.isFollowed;

            return instance;
        } else {
            return null;
        }
    }
}
